import type { CommissionCategory } from '@travel-planner/shared/pricing';
import type { CommissionRates, CommissionRateTexts } from './commission-rates';
import { COMMISSION_CATEGORIES } from '@travel-planner/shared/pricing';
import { useId } from 'react';
import { PercentInput } from '../percent-input';
import css from './commission-deal-grid.module.scss';
import { COMMISSION_CATEGORY_LABELS } from './commission-rates';

export type CommissionReferenceRow = {
  label: string;
  rates: CommissionRates;
};

type CommissionDealGridProps = {
  /** Read-only rows above the input, e.g. the application's and the organisation's. */
  references: readonly CommissionReferenceRow[];
  /** The input row's label, e.g. "Agency specific". */
  inputLabel: string;
  value: CommissionRateTexts;
  onChange: (next: CommissionRateTexts) => void;
  /** Categories whose text is not a rate from 0 to 100. */
  invalid?: readonly CommissionCategory[];
  disabled?: boolean;
  /** The rate that will be used, shown in bold under the input. */
  result?: CommissionReferenceRow;
};

const rateText = (rate: number | undefined) =>
  typeof rate === 'number' ? `${rate}%` : '-';

/** XT-1039 — a commission per category, under the rates it is weighed against. */
export const CommissionDealGrid = ({
  references,
  inputLabel,
  value,
  onChange,
  invalid = [],
  disabled,
  result,
}: CommissionDealGridProps) => {
  const id = useId();
  return (
    <div className={css.root}>
      <table className={css.rootTable}>
        <thead>
          <tr>
            <td />
            {COMMISSION_CATEGORIES.map((category) => (
              <th className={css.rootHead} key={category} scope="col">
                {COMMISSION_CATEGORY_LABELS[category]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {references.map((row) => (
            <tr key={row.label}>
              <th className={css.rootLabel} scope="row">
                {row.label}
              </th>
              {COMMISSION_CATEGORIES.map((category) => (
                <td className={css.rootRate} key={category}>
                  {rateText(row.rates[category])}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <th className={css.rootLabel} scope="row">
              {inputLabel}
            </th>
            {COMMISSION_CATEGORIES.map((category) => {
              const inputId = `${id}-${category}`;
              const isInvalid = invalid.includes(category);
              return (
                <td className={css.rootInput} key={category}>
                  <label className={css.rootHidden} htmlFor={inputId}>
                    {`${COMMISSION_CATEGORY_LABELS[category]} — ${inputLabel}`}
                  </label>
                  <PercentInput
                    disabled={disabled}
                    id={inputId}
                    onChange={(next) =>
                      onChange({ ...value, [category]: next })
                    }
                    value={value[category]}
                  />
                  {isInvalid ? (
                    <span className={css.rootError}>Enter 0 to 100</span>
                  ) : null}
                </td>
              );
            })}
          </tr>
        </tbody>
        {result ? (
          <tfoot>
            <tr className={css.rootResult}>
              <th className={css.rootLabel} scope="row">
                {result.label}
              </th>
              {COMMISSION_CATEGORIES.map((category) => (
                <td className={css.rootRate} key={category}>
                  {rateText(result.rates[category])}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
};
